<template>
    <q-layout view="hHh LpR fFf">
            <q-header 
                bordered 
                v-if="$slots.header"
                :class="{'bg-dark text-grey-12': $q.dark.isActive}"
            >
                <q-toolbar class="q-pa-none">
                    <div class="q-ma-sm">
                        <q-btn 
                            dense
                            :icon="`mdi-${sidebar.left.open ? 'backburger' : 'menu'}`"
                            @click="sidebar.left.open = !sidebar.left.open"
                            :size="$q.screen.lt.md ? 'sm' : 'md'"
                        />
                    </div>

                    <q-toolbar-title
                        :class="{ 'text-subtitle1': $q.screen.lt.md }"
                    >
                        <slot name="header" :sidebar="sidebar" />
                    </q-toolbar-title>

                    <div class="text-center q-mr-sm">
                        <q-item-label class="text-bold">
                            {{ time.text }}
                        </q-item-label>
                        <q-item-label caption :class="{'text-grey-7': $q.dark.isActive, 'text-grey-4': !$q.dark.isActive}">{{ getTimezone() }}</q-item-label>
                    </div>

                    <q-btn-dropdown :dense="$q.screen.lt.lg" class="text-white" flat no-caps>
                        <template #label>
                            <q-avatar rounded :style="`background-color: ${(getUser().avatar_path || !fallbackAvatar) ? 'transparent' : '#009688'};`" class="q-mr-sm">
                                <template v-if="!getUser().avatar_path || fallbackAvatar">
                                    {{ getUser().name.charAt(0) }}
                                </template>
                                <img v-else @error="fallbackAvatar = true" :src="getUser().avatar_path">
                            </q-avatar>

                            <q-list v-if="$q.screen.gt.md">
                                <q-item class="q-pl-none text-center">
                                    <q-item-section>
                                        <q-item-label>{{ getUser().name }}</q-item-label>
                                        <q-item-label caption class="text-blue-grey-2">{{ GetRole() }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </template>
                        <q-card>
                            <q-list v-if="$q.screen.lt.lg">
                                <q-item>
                                    <q-item-section avatar>
                                        <q-avatar rounded :style="`color: white;background-color: ${getUser().avatar_path ? 'transparent' : '#009688'};`" class="q-mr-sm">
                                            <template v-if="!getUser().avatar_path">
                                                {{ getUser().name.charAt(0) }}
                                            </template>
                                            <img v-else :src="getUser().avatar_path">
                                        </q-avatar>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{ getUser().name }}</q-item-label>
                                        <q-item-label caption class="text-blue-grey-2">{{ GetRole() }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>

                            <q-btn-group separator spread v-if="$q.screen.lt.lg">
                                <q-btn
                                    size="sm"
                                    flat
                                    class="full-height"
                                    :icon="`mdi-${darkmode ? 'white-balance-sunny' : 'moon-waning-crescent'}`"
                                    @click="darkmode = !darkmode"
                                />
                                <q-separator vertical />
                                <q-btn 
                                    size="sm"
                                    flat
                                    :icon="`mdi-bell${sidebar.right.open ? '-ring' : ''}`"
                                    @click="sidebar.right.open = !sidebar.right.open"
                                    v-close-popup
                                >
                                    <q-badge v-if="$page.props.auth.notifications?.count ?? false" color="red">
                                        {{ $page.props.auth.notifications.count > 9 ? '9+' : $page.props.auth.notifications.count }}
                                    </q-badge>
                                </q-btn>
                            </q-btn-group>

                            <q-separator v-if="$q.screen.lt.lg" />

                            <q-card-section class="q-pa-none">
                                <q-list dense>
                                    <q-item clickable @click="profileDialogOpen = true">
                                        <q-item-section avatar>
                                            <q-icon name="mdi-account" />
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label>Profile</q-item-label>
                                        </q-item-section>
                                    </q-item>
                                    <q-separator />
                                    <q-item clickable @click="$inertia.post(route('logout'))">
                                        <q-item-section avatar>
                                            <q-icon name="mdi-logout" />
                                        </q-item-section>
                                        <q-item-section>
                                            Logout
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                            </q-card-section>
                        </q-card>
                    </q-btn-dropdown>
                    
                    <q-btn
                        flat
                        class="full-height q-mx-sm"
                        :icon="`mdi-${darkmode ? 'white-balance-sunny' : 'moon-waning-crescent'}`"
                        @click="darkmode = !darkmode"
                        v-if="$q.screen.gt.md"
                    />

                    <q-btn
                        v-if="isImpersonating"
                        flat
                        class="full-height q-mx-sm"
                        icon="mdi-account-switch"
                        color="warning"
                        @click="stopImpersonating"
                    >
                        <q-tooltip>
                            Impersonating {{ $page.props.auth.user.name }} - Click to return to your account
                        </q-tooltip>
                    </q-btn>

                    <div class="q-mx-sm" v-if="$q.screen.gt.md">
                        <q-btn 
                            flat
                            :icon="'mdi-bullhorn' + (sidebar.bulletin.open ? '-outline' : '')"
                            @click="sidebar.bulletin.open = !sidebar.bulletin.open"
                        >
                            <q-badge v-if="BulletinStore.unread" floating color="red">
                                {{ BulletinStore.unread  > 9 ? '9+' : BulletinStore.unread }}
                            </q-badge>
                        </q-btn>
                    </div>
                    
                    <div class="q-ml-sm q-mr-md" v-if="isAdmin() && $q.screen.gt.md">
                        <q-btn 
                            flat
                            :icon="`mdi-bell${sidebar.notifications.open ? '-ring' : ''}`"
                            @click="sidebar.notifications.open = !sidebar.notifications.open"
                        >
                            <q-badge v-if="NotificationStore.count" floating color="red">
                                {{ NotificationStore.count  > 9 ? '9+' : NotificationStore.count }}
                            </q-badge>
                        </q-btn>
                    </div>
                </q-toolbar>
            </q-header>

            <q-drawer 
                bordered
                side="left" 
                :width="300"
                :class="{ 'bg-grey-1': !$q.dark.isActive }"
                :breakpoint="800"
                :behavior="$q.screen.width > 800 ? 'desktop' : 'mobile'"
                v-model="sidebar.left.open"
            >
                <div style="width: 100%;height: 100%;display: flex !important;flex-direction: column;">
                    <q-list>
                        <template v-for="(item, i) in sidebar.left.qitems">
                            <template v-if="item.subItems ?? false">
                                <q-expansion-item
                                    :model-value="isExpanded(item)"
                                    :icon="item.icon"
                                    :label="item.label"
                                    :header-class="{ 'q-item--active': isExpanded(item) }"
                                >
                                    <q-list
                                        bordered
                                        :class="{ 'bg-grey-3': !$q.dark.isActive }"
                                        style="border-right: none;border-top: none;"
                                    >
                                        <q-item
                                            v-for="(subItem, y) in item.subItems"
                                            v-ripple
                                            clickable
                                            :active="route().current(_override(item, y))"
                                            :active-class="`text-${$q.dark.isActive ? 'blue-4' : 'primary'}`"
                                            @click="_visit(_override(item, y))"
                                            @click.middle="_visit(_override(item, y), true)"
                                        >
                                            <q-item-section avatar>
                                                <q-icon :name="subItem.icon" />
                                            </q-item-section>
                                            <q-item-section>
                                                {{subItem.label}}
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                </q-expansion-item>
                            </template>
                            <template v-else>
                                <q-item 
                                    v-ripple
                                    clickable
                                    :active="route().current(item.route)"
                                    :active-class="`text-${$q.dark.isActive ? 'blue-4' : 'primary'}`"
                                    @click="_visit(item.route)"
                                    @click.middle="_visit(item.route, true)"
                                >
                                    <q-item-section avatar>
                                        <q-icon :name="item.icon" />
                                    </q-item-section>
                                    <q-item-section>
                                        {{item.label}}
                                    </q-item-section>
                                </q-item>
                            </template>
                        </template>

                        <q-item 
                            v-if="isAdmin()"
                            v-ripple
                            clickable
                            :active="payrollDialog.open"
                            @click="payrollDialog.open = !payrollDialog.open"
                        >
                            <q-item-section avatar>
                                <q-icon name="mdi-export-variant" />
                            </q-item-section>
                            <q-item-section>
                                Generate Payroll
                            </q-item-section>
                        </q-item>
                    </q-list>
                    
                    <q-separator style="margin-top: auto;" />
                    <q-item :class="{'bg-blue-grey-1':!$q.dark.isActive, 'q-py-md text-grey-7 text-center': 1}">
                        <q-item-section class="no-wrap">
                            <q-item-label>
                                <!-- <a href="https://lowcountrytech.com/" target="_BLANK" class="text-blue-6" style="text-decoration: none;">LCT</a> &copy; 2022<template v-if="new Date().getFullYear() !== 2022">-{{ new Date().getFullYear() }}</template> -->
                                <span class="text-blue-6">Hexagon Labs</span> &copy; 2022<template v-if="new Date().getFullYear() !== 2022">-{{ new Date().getFullYear() }}</template>
                            </q-item-label>
                            <q-item-label style="font-size: 0.6rem;opacity: 0.85;">
                                v{{ $page.props.auth.panel.version }}-{{ $page.props.auth.panel.build }} <!-- - <a href="#" class="text-blue-6" style="text-decoration: none;">Report Issue</a> -->
                            </q-item-label>
                        </q-item-section>
                    </q-item>
                </div>
            </q-drawer>

            <NotificationsSidebar 
                v-if="isAdmin() || isManager()"
                v-model="sidebar.notifications.open"
            />

            <BulletinSidebar
                v-model:open="sidebar.bulletin.open"
            />

            <q-page-container >
                <q-page class="q-pa-md">
                    <q-scroll-area
                        visible
                        class="absolute-full fit scroll overflow-hidden"
                        style="height: calc(100vh - 60px);"
                        ref="pageScrollArea"
                    >
                        <main>
                            <slot />
                        </main>
                    </q-scroll-area>
                </q-page>
            </q-page-container>

            <q-dialog v-model="payrollDialog.open" v-if="isAdmin()">
                <q-card
                    :square="$q.dark.isActive"
                    :flat="$q.dark.isActive"
                    :bordered="$q.dark.isActive"
                >
                    <q-toolbar :class="{'bg-primary text-white': !$q.dark.isActive}">
                        <q-toolbar-title>Generate Payroll</q-toolbar-title>
                        
                        <q-btn
                            flat
                            round
                            v-close-popup
                            icon="mdi-close"
                        />
                    </q-toolbar>

                    <q-separator v-if="$q.dark.isActive" />

                    <q-card-section class="q-pa-none">
                        <q-item>
                            <q-item-section avatar>
                                <q-btn
                                    round
                                    size="sm"
                                    icon="mdi-calendar-range"
                                >
                                    <q-popup-proxy 
                                        @before-show="dateProxy()" 
                                        cover 
                                        transition-show="scale" 
                                        transition-hide="scale"
                                    >
                                        <q-date 
                                            today-btn
                                            mask="YYYY-MM-DD"
                                            :range="payrollDialog.range"
                                            v-model="payrollDialog.proxyDate"
                                        >
                                            <div class="row items-center justify-end q-gutter-sm">
                                                <q-btn label="Cancel" color="primary" flat v-close-popup />
                                                <q-btn label="OK" color="primary" flat @click="dateProxy(true)" v-close-popup />
                                            </div>
                                        </q-date>
                                    </q-popup-proxy>
                                </q-btn>
                            </q-item-section>
                            <template v-if="payrollDialog.range">
                                <q-item-section>
                                    <q-input
                                        dense
                                        readonly
                                        :model-value="payrollDialog.date.from"
                                        label="From"
                                        placeholder="From"
                                        type="date"
                                        class="q-pa-none"
                                        :rules="[{ required: true, message: 'This field is required' }]"
                                    />
                                </q-item-section>
                                <q-item-section>
                                    <q-input
                                        dense
                                        readonly
                                        :model-value="payrollDialog.date.to"
                                        label="To"
                                        placeholder="To"
                                        type="date"
                                        class="q-pa-none"
                                        :rules="[{ required: true, message: 'This field is required' }]"
                                    />
                                </q-item-section>
                            </template>
                            <q-item-section v-else>
                                <q-input
                                    dense
                                    readonly
                                    :model-value="payrollDialog.date"
                                    label="Day"
                                    placeholder="Day"
                                    type="date"
                                    class="q-pa-none"
                                    :rules="[{ required: true, message: 'This field is required' }]"
                                />
                            </q-item-section>
                        </q-item>
                    </q-card-section>
                    <q-item>
                        <q-item-section>
                            <q-item-label>
                                <q-checkbox 
                                    label="Range"
                                    :model-value="payrollDialog.range" 
                                    @update:model-value="handleRangeChange($event)"
                                >
                                    <q-tooltip>
                                        <span v-if="!payrollDialog.range">Click to generate payroll for a range of days</span>
                                        <span v-else>Click to generate payroll for a single day</span>
                                    </q-tooltip>
                                </q-checkbox>
                            </q-item-label>
                            <q-item-label>
                                <q-checkbox
                                    label="Roster Export"
                                    v-model="payrollDialog.rosterExport"
                                >
                                    <q-tooltip>
                                        Known as the "Lumper Work Items" Export
                                    </q-tooltip>
                                </q-checkbox>
                            </q-item-label>
                        </q-item-section>
                        <q-item-section side>
                            <q-item-label>
                                <q-btn
                                    label="Generate"
                                    color="primary"
                                    @click="generatePayroll"
                                />
                            </q-item-label>
                            <q-item-label>
                                <q-btn
                                    flat
                                    label="Cancel"
                                    v-close-popup
                                />
                            </q-item-label>
                        </q-item-section>
                    </q-item>

                    <q-inner-loading :showing="payrollDialog.loading">
                        <q-spinner size="5px" />
                    </q-inner-loading>
                </q-card>
            </q-dialog>
            
            <WorkOrderDialog />

            <ProfileDialog ref="profileDialog" v-model="profileDialogOpen" @updated="updatedProfile" />
        </q-layout>
</template>

<script>
import ProfileDialog from '@/Components/Dialogs/ProfileDialog/index.vue';
import WorkOrderDialog from '@/Components/Dialogs/NewWorkOrderDialog/WorkOrderDialog.vue';

import { useForm, router } from '@inertiajs/vue3';
import { useBulletinStore } from '@/Stores/BulletinStore';
import { useNotificationStore } from '@/Stores/NotificationStore.js';

import BulletinSidebar from '@/Components/Sidebars/BulletinSidebar.vue';
import NotificationsSidebar from '@/Components/Sidebars/NotificationSidebar.vue';

export default {
    name: 'Authenticated',
    setup() {
        const BulletinStore = useBulletinStore();
        const NotificationStore = useNotificationStore();

        return {
            BulletinStore,
            NotificationStore,
        };
    },
    provide() {
        return {
            scrollTo: this.scrollTo,
            scrollToElement: this.scrollToElement,
        }
    },
    props: {
        errors: Object,
    },
    components: {
        ProfileDialog,
        BulletinSidebar,
        NotificationsSidebar,
        WorkOrderDialog,
    },
    data() {
        return {
            time: {
                text: '',
                interval: null,
            },
            fallbackAvatar: false,
            profileDialogOpen: false,
            sidebar: {
                left: {
                    open: this.$q.screen.width > 800,
                    expandedItems: [],
                    qitems: this.$page.props.auth?.nav ?? [],
                },
                right: {
                    lastCall: null,
                    wait: 3000,
                },
                notifications: {
                    open: false,
                    count: 0,
                },
                bulletin: {
                    open: false,
                    count: 0,
                },
            },
            payrollDialog: useForm({
                loading: false,
                open: false,
                range: true,
                rosterExport: false,
                date: {
                    from: this.calendarWeek().sunday,
                    to: this.calendarWeek().saturday,
                },
                proxyDate: {
                    from: this.calendarWeek().sunday,
                    to: this.calendarWeek().saturday,
                },
            })
        };
    },
    methods: {
        stopImpersonating() {
            router.post(route('impersonate.stop'));
        },
        scrollTo(pxLocation) {
            this.$refs.pageScrollArea
                .setScrollPosition('vertical', Math.abs(pxLocation), 300);
        },
        scrollToElement(targetId) {
            const target = document.getElementById(targetId);

            if (!target) {
                console.error(`Element with id ${targetId} not found!`);
                return;
            }
            
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
        },
        fetchAuth() {
            return new Promise((resolve, reject) => {
                if (
                    Date.now() - this.sidebar.right.lastCall <=
                    this.sidebar.right.wait
                ) {
                    return resolve();
                }

                const $this = this;
                this.$inertia.reload({
                    only: ['auth'],
                    onSuccess() {
                        $this.sidebar.right.lastCall = Date.now();
                        resolve();
                    },
                    onError(error) {
                        reject(error);
                    },
                });
            });
        },
        _visit(routeName, newTab = false) {
            if (newTab) {
                const url = this.route(routeName);
                window.open(url, '_blank');
                return;
            }

            this.$inertia.visit(this.route(routeName), {
                data: {},
                onFinish: (q) => {
                    if (this.$q.screen.width <= 800) {
                        this.sidebar.left.open = false;
                    }
                },
            });
        },
        updatedProfile() {
            this.$inertia.reload({
                only: ['auth'],
                onSuccess() {
                    this.$refs.profileDialog.reset();
                },
            });
        },
        _override(item, subitemIndex) {
            return (item.subItems[subitemIndex].route + '').includes('.')
                ? item.subItems[subitemIndex].route
                : `${item.route}.${item.subItems[subitemIndex].route}`;
        },
        isSubItemActive(item) {
            if (item.subItems) {
                for (let i = 0; i < item.subItems.length; i++) {
                    if (this.route().current(this._override(item, i))) {
                        return true;
                    }
                }
            }
            return false;
        },
        isExpanded(item) {
            return (
                this.sidebar.left.expandedItems.includes(item.route) ||
                this.isSubItemActive(item)
            );
        },
        generatePayroll() {
            const searchParams = new URLSearchParams();
            if (this.payrollDialog.rosterExport) {
                searchParams.append('workItemExport', 1);
            }

            if (this.payrollDialog.range) {
                searchParams.append('range', 1);
                searchParams.append('to', this.payrollDialog.date.to);
                searchParams.append('from', this.payrollDialog.date.from);
            } else {
                searchParams.append('range', 0);
                searchParams.append('date', this.payrollDialog.date);
            }

            window.open(
                `${window.location.origin}/work-order/payroll?${searchParams.toString()}`,
                '_blank',
            );
        },
        handleRangeChange(value) {
            if (!value) {
                this.payrollDialog.proxyDate =
                    this.payrollDialog.date = this.calendarWeek().sunday;

                this.payrollDialog.range = value;
                return;
            }

            this.payrollDialog.date = 
                this.payrollDialog.proxyDate = {
                    from: this.calendarWeek().sunday,
                    to: this.calendarWeek().saturday,
                };

            this.payrollDialog.range = value;
        },
        resetDialog() {
            this.payrollDialog = {
                loading: false,
                open: false,
                date: {
                    from: this.calendarWeek().sunday,
                    to: this.calendarWeek().saturday,
                },
                proxyDate: {
                    from: this.calendarWeek().sunday,
                    to: this.calendarWeek().saturday,
                },
            };
        },
        dateProxy(save = false) {
            if (save) {
                this.payrollDialog.date =
                    this.payrollDialog.proxyDate;
                return;
            }
            this.payrollDialog.proxyDate =
                this.payrollDialog.date;
        },
    },
    mounted() {
        this.$nextTick(() => {
            if (this.isAdmin() || this.isManager()) this.NotificationStore.fetchCount();

            this.BulletinStore.fetchCount();

            this.time.text = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            
            setInterval(() => {
                this.time.text = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            }, 1000);

            // if (import.meta.env.DEV) {
            //     console.log('Authenticated.vue mounted');
            //     // We're going to load the WorkOrderDialog from the WorkOrderDialogStore
            //     // 25862
            //     // 29
            //     this.workOrderDialogStore.openDialog(25824, 46);
            // }
        });
    },
    beforeDestroy() {
        clearInterval(this.time.interval);
    },
    watch: {
        'sidebar.notifications.open'(val) {
            if (val) {
                if (this.sidebar.bulletin.open) {
                    this.sidebar.bulletin.open = false;
                }

                this.sidebar.notifications.loading = true;

                this.fetchAuth().finally(() => {
                    this.sidebar.notifications.loading = false;
                });
            }
        },
        'sidebar.bulletin.open'(val) {
            if (val) {
                if (this.sidebar.notifications.open) {
                    this.sidebar.notifications.open = false;
                }
                
                this.sidebar.bulletin.loading = true;

                this.fetchAuth().finally(() => {
                    this.sidebar.bulletin.loading = false;
                });
            }
        },
        'payrollDialog.open'(val) {
            if (!val) {
                this.resetDialog();
            }
        }
    },
    computed: {
        isImpersonating() {
            return this.$page.props.impersonating?.active === true;
        },
        darkmode: {
            get() {
                return this.$q.dark.isActive;
            },
            set(val) {
                this.$q.dark.set(val);
                this.$q.cookies.set('preferred-theme', val ? 'dark' : 'light', {
                    expires: 365*24*60*60,
                    path: '/',
                    sameSite: 'Strict'
                });
            },
        },
    },
};
</script>